import React from "react";
import { motion } from "framer-motion";
import { 
  Check, 
  X, 
  Zap,
  Phone,
  FileText,
  MessageSquare,
  Award,
  Calendar,
  Shield,
  TrendingUp,
  DollarSign
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../ui/table";
import { Card } from "../ui/card";

const AshaVsWeaveSlide = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  const features = [
    { 
      name: (
        <div>
          <span className="font-bold text-slate-900">Communication Suite</span>
          <div className="text-sm text-slate-500 font-normal mt-0.5">
            VoIP Phones, Phone Tree, Call Queue, Analytics, Team Chat, Fax
          </div>
        </div>
      ),
      asha: true, 
      weave: true, 
      icon: Phone 
    },
    { 
      name: (
        <div>
          <span className="font-bold text-slate-900">Patient Engagement</span>
          <div className="text-sm text-slate-500 font-normal mt-0.5">
            2-Way Texting, Reviews, Bulk SMS, Missed Call Text
          </div>
        </div>
      ),
      asha: true, 
      weave: true, 
      icon: MessageSquare 
    },
    { 
      name: (
        <div>
          <span className="font-bold text-slate-900">Scheduling Automation</span>
          <div className="text-sm text-slate-500 font-normal mt-0.5">
            Reminders, Confirmations, Recall, ASAP & Overdue Lists
          </div>
        </div>
      ),
      asha: true, 
      weave: true, 
      icon: Calendar 
    },
    { 
      name: (
        <div>
          <span className="font-bold text-slate-900">Forms & Payments</span>
          <div className="text-sm text-slate-500 font-normal mt-0.5">
            Digital Forms, Text-to-Pay, Payments
          </div>
        </div>
      ),
      asha: true, 
      weave: true, 
      icon: FileText 
    },
    { name: "AI Receptionist (24/7 Answering)", asha: true, weave: false, icon: Phone }, // Updated: Weave has no AI Receptionist
    { name: "AI Insurance Verification", asha: true, weave: false, icon: Shield },
    { name: "AI Insurance Caller", asha: true, weave: false, icon: Phone },
    { name: "AI Hygiene Recall Specialist", asha: true, weave: false, icon: Calendar },
    { name: "AI Schedule Optimizer", asha: true, weave: false, icon: Zap },
    { name: "AI Case Acceptance Agent", asha: true, weave: false, icon: Award },
    { name: "AI Patient Experience Agent", asha: true, weave: false, icon: TrendingUp },
  ];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-8 lg:p-12 font-sans overflow-hidden">
      <motion.div 
        className="flex flex-row h-full gap-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left Column: Header + TLDR */}
        <div className="w-[35%] flex flex-col gap-6 justify-center">
            <motion.div variants={itemVariants} className="text-left">
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight leading-tight">
                Asha Health <span className="text-slate-400 font-light mx-2">vs</span> <br/>Weave
              </h2>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-3xl p-8 shadow-sm">
              <div>
                <h3 className="text-blue-900 font-bold text-2xl mb-4">Bottom Line</h3>
                <p className="text-blue-800 leading-relaxed text-xl mb-4">
                  Asha AI has all of the features that Weave does but also comes with <span className="font-bold">8 AI agents</span> to automate functions in your front office that Weave doesn't have.
                </p>
                <p className="text-blue-800 leading-relaxed text-xl">
                   The pricing is fairly similar between the two. With Asha Health, you don't need to have separate communication suite and many separate AI platforms. We bring it all under one roof.
                </p>
              </div>
            </motion.div>
        </div>

        {/* Right Column: Feature Stack Comparison */}
        <motion.div variants={itemVariants} className="w-[65%] flex flex-col h-full">
           <Card className="h-full border-slate-200 shadow-sm overflow-hidden flex flex-col bg-white">
              <div className="p-5 pb-2 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                  <h3 className="font-bold text-lg text-slate-700">Feature Breakdown</h3>
                  <div className="flex gap-16 pr-8">
                      <div className="w-20 text-center font-bold text-blue-600 text-lg">Asha</div>
                      <div className="w-20 text-center font-bold text-purple-600 text-lg">Weave</div>
                  </div>
              </div>
              <div className="flex-1 overflow-y-auto p-1 custom-scrollbar">
                  <Table>
                      <TableBody>
                          {features.map((feature, idx) => (
                              <TableRow key={idx} className="hover:bg-slate-50 border-b-slate-50 last:border-0">
                                  <TableCell className="font-medium text-slate-700 text-base py-3 pl-6 flex items-center gap-3">
                                      <div className={`p-1.5 rounded-md ${feature.asha && !feature.weave ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                          <feature.icon className="w-4 h-4" />
                                      </div>
                                      {feature.name}
                                  </TableCell>
                                  <TableCell className="w-20 text-center">
                                      <div className="flex justify-center">
                                          {feature.asha ? (
                                              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                                  <Check className="w-5 h-5 text-green-600" />
                                              </div>
                                          ) : (
                                              <X className="w-5 h-5 text-slate-300" />
                                          )}
                                      </div>
                                  </TableCell>
                                  <TableCell className="w-20 text-center pr-8">
                                      <div className="flex justify-center">
                                          {feature.weave ? (
                                              <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                  <Check className="w-5 h-5 text-purple-600" />
                                              </div>
                                          ) : (
                                              <X className="w-5 h-5 text-red-200" />
                                          )}
                                      </div>
                                  </TableCell>
                              </TableRow>
                          ))}
                          {/* Monthly Cost Row */}
                          <TableRow className="bg-slate-50/80 font-bold border-t-2 border-slate-200">
                              <TableCell className="text-lg py-4 pl-6 flex items-center gap-3">
                                  <div className="p-1.5 rounded-md bg-emerald-100 text-emerald-600">
                                      <DollarSign className="w-5 h-5" />
                                  </div>
                                  Monthly Investment
                              </TableCell>
                              <TableCell className="w-20 text-center">
                                  <div className="text-blue-600 text-2xl font-black">$799</div>
                                  <div className="text-[10px] text-slate-500 font-medium mt-0.5 uppercase tracking-tighter">All-Inclusive</div>
                              </TableCell>
                              <TableCell className="w-20 text-center pr-8">
                                  <div className="text-purple-600 text-2xl font-black">$750+</div>
                                  <div className="text-[10px] text-slate-500 font-medium mt-0.5 uppercase tracking-tighter">Ultimate + Add-ons</div>
                              </TableCell>
                          </TableRow>
                      </TableBody>
                  </Table>
              </div>
           </Card>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AshaVsWeaveSlide;

